'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast, Toaster } from 'sonner';
import Icon from '@/components/ui/AppIcon';
import Breadcrumb from '@/components/Breadcrumb';

// Create supabase client once outside the component to avoid infinite re-render
const supabase = createClient();

interface ProfileData {
  id: string;
  email: string;
  full_name: string;
  role: string;
  reparto: string;
  sede: string;
  telefono: string;
  qualifica: string;
  bio: string;
  avatar_url: string;
  notifiche_email: boolean;
  notifiche_sms: boolean;
  notifiche_inapp: boolean;
  lingua: string;
  tema: string;
  two_fa_enabled: boolean;
  ultimo_accesso: string | null;
  created_at: string;
}

const ROLE_LABELS: Record<string, string> = {
  super_admin: 'Super Admin',
  admin_it: 'Admin IT',
  tecnico: 'Tecnico',
  operatore: 'Operatore',
  sola_lettura: 'Sola Lettura',
};

export default function ProfilePage() {
  const { user, profile: authProfile, roleLabel } = useAuth();
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [tab, setTab] = useState<'profilo' | 'sicurezza' | 'notifiche'>('profilo');
  const [form, setForm] = useState({
    full_name: '',
    telefono: '',
    reparto: '',
    sede: '',
    qualifica: '',
    bio: '',
  });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [notifForm, setNotifForm] = useState({
    notifiche_email: true,
    notifiche_sms: false,
    notifiche_inapp: true,
  });

  const fetchProfile = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', user.id)
        .single();
      if (error) throw error;
      const p = data as ProfileData;
      setProfile(p);
      setForm({
        full_name: p.full_name || '',
        telefono: p.telefono || '',
        reparto: p.reparto || '',
        sede: p.sede || '',
        qualifica: p.qualifica || '',
        bio: p.bio || '',
      });
      setNotifForm({
        notifiche_email: p.notifiche_email ?? true,
        notifiche_sms: p.notifiche_sms ?? false,
        notifiche_inapp: p.notifiche_inapp ?? true,
      });
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Errore caricamento profilo');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const saveProfile = async () => {
    if (!user) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({
          full_name: form.full_name,
          telefono: form.telefono,
          reparto: form.reparto,
          sede: form.sede,
          qualifica: form.qualifica,
          bio: form.bio,
          updated_at: new Date().toISOString(),
        })
        .eq('id', user.id);
      if (error) throw error;
      toast.success('Profilo aggiornato con successo');
      await fetchProfile();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const saveNotifiche = async () => {
    if (!user) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({
          notifiche_email: notifForm.notifiche_email,
          notifiche_sms: notifForm.notifiche_sms,
          notifiche_inapp: notifForm.notifiche_inapp,
          updated_at: new Date().toISOString(),
        })
        .eq('id', user.id);
      if (error) throw error;
      toast.success('Preferenze notifiche salvate');
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async () => {
    if (!passwordForm.newPassword || !passwordForm.confirmPassword) {
      toast.error('Compila tutti i campi');
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('Le password non coincidono');
      return;
    }
    if (passwordForm.newPassword.length < 8) {
      toast.error('La password deve essere di almeno 8 caratteri');
      return;
    }
    setChangingPassword(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: passwordForm.newPassword,
      });
      if (error) throw error;
      toast.success('Password aggiornata con successo');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Errore cambio password');
    } finally {
      setChangingPassword(false);
    }
  };

  const initials = profile?.full_name
    ? profile.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : profile?.email?.slice(0, 2).toUpperCase() || 'UT';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full py-20">
        <div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-full bg-background p-6 space-y-6">
      <Toaster position="bottom-right" theme="dark" richColors />
      <Breadcrumb items={[{ label: 'Profilo Utente' }]} />

      {/* Header */}
      <div className="flex items-center gap-5">
        <div className="w-16 h-16 rounded-2xl bg-sky-500/20 flex items-center justify-center flex-shrink-0">
          <span className="text-2xl font-bold text-sky-400">{initials}</span>
        </div>
        <div>
          <h1 className="text-2xl font-bold text-white">{profile?.full_name || 'Utente'}</h1>
          <div className="flex items-center gap-3 mt-1">
            <span className="text-sm text-muted-color">{profile?.email}</span>
            <span className="text-xs bg-sky-500/20 text-sky-400 px-2 py-0.5 rounded-full font-semibold">
              {ROLE_LABELS[profile?.role || ''] || profile?.role}
            </span>
          </div>
          {profile?.ultimo_accesso && (
            <p className="text-xs text-muted-color mt-0.5">
              Ultimo accesso: {new Date(profile.ultimo_accesso).toLocaleString('it-IT')}
            </p>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-surface rounded-xl p-1 border border-border w-fit">
        {[
          { v: 'profilo', l: 'Dati Personali', icon: 'UserCircleIcon' },
          { v: 'sicurezza', l: 'Sicurezza', icon: 'ShieldCheckIcon' },
          { v: 'notifiche', l: 'Notifiche', icon: 'BellIcon' },
        ].map(({ v, l, icon }) => (
          <button
            key={v}
            onClick={() => setTab(v as any)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${tab === v ? 'bg-sky-500/20 text-sky-400' : 'text-muted-color hover:text-white'}`}
          >
            <Icon name={icon as any} size={16} />
            {l}
          </button>
        ))}
      </div>

      {/* Tab: Profilo */}
      {tab === 'profilo' && (
        <div className="bg-surface border border-border rounded-2xl p-6 space-y-5 max-w-2xl">
          <h2 className="text-base font-bold text-white">Dati Personali</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-muted-color mb-1.5">Nome Completo</label>
              <input
                value={form.full_name}
                onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))}
                className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-sm text-white placeholder-muted-color focus:outline-none focus:border-sky-500"
                placeholder="Nome Cognome"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-muted-color mb-1.5">Telefono</label>
              <input
                value={form.telefono}
                onChange={e => setForm(f => ({ ...f, telefono: e.target.value }))}
                className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-sm text-white placeholder-muted-color focus:outline-none focus:border-sky-500"
                placeholder="+39 000 000 0000"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-muted-color mb-1.5">Reparto</label>
              <input
                value={form.reparto}
                onChange={e => setForm(f => ({ ...f, reparto: e.target.value }))}
                className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-sm text-white placeholder-muted-color focus:outline-none focus:border-sky-500"
                placeholder="Reparto IT"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-muted-color mb-1.5">Sede</label>
              <input
                value={form.sede}
                onChange={e => setForm(f => ({ ...f, sede: e.target.value }))}
                className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-sm text-white placeholder-muted-color focus:outline-none focus:border-sky-500"
                placeholder="Sede principale"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-muted-color mb-1.5">Qualifica</label>
              <input
                value={form.qualifica}
                onChange={e => setForm(f => ({ ...f, qualifica: e.target.value }))}
                className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-sm text-white placeholder-muted-color focus:outline-none focus:border-sky-500"
                placeholder="Sistemista Senior"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-muted-color mb-1.5">Bio</label>
              <textarea
                value={form.bio}
                onChange={e => setForm(f => ({ ...f, bio: e.target.value }))}
                rows={3}
                className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-sm text-white placeholder-muted-color focus:outline-none focus:border-sky-500 resize-none"
                placeholder="Breve descrizione..."
              />
            </div>
          </div>
          <div className="flex justify-end">
            <button
              onClick={saveProfile}
              disabled={saving}
              className="flex items-center gap-2 bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white px-5 py-2 rounded-lg text-sm font-semibold transition-colors"
            >
              {saving ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Icon name="CheckIcon" size={16} />
              )}
              Salva Modifiche
            </button>
          </div>
        </div>
      )}

      {/* Tab: Sicurezza */}
      {tab === 'sicurezza' && (
        <div className="bg-surface border border-border rounded-2xl p-6 space-y-5 max-w-2xl">
          <h2 className="text-base font-bold text-white">Cambio Password</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-muted-color mb-1.5">Nuova Password</label>
              <input
                type="password"
                value={passwordForm.newPassword}
                onChange={e => setPasswordForm(f => ({ ...f, newPassword: e.target.value }))}
                className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-sm text-white placeholder-muted-color focus:outline-none focus:border-sky-500"
                placeholder="Minimo 8 caratteri"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-muted-color mb-1.5">Conferma Nuova Password</label>
              <input
                type="password"
                value={passwordForm.confirmPassword}
                onChange={e => setPasswordForm(f => ({ ...f, confirmPassword: e.target.value }))}
                className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-sm text-white placeholder-muted-color focus:outline-none focus:border-sky-500"
                placeholder="Ripeti la nuova password"
              />
            </div>
          </div>
          <div className="flex justify-end">
            <button
              onClick={changePassword}
              disabled={changingPassword}
              className="flex items-center gap-2 bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white px-5 py-2 rounded-lg text-sm font-semibold transition-colors"
            >
              {changingPassword ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Icon name="LockClosedIcon" size={16} />
              )}
              Aggiorna Password
            </button>
          </div>

          {/* Account Info */}
          <div className="border-t border-border pt-5 space-y-3">
            <h3 className="text-sm font-bold text-white">Informazioni Account</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-muted-color text-xs">Email</p>
                <p className="text-white font-medium">{profile?.email}</p>
              </div>
              <div>
                <p className="text-muted-color text-xs">Ruolo</p>
                <p className="text-white font-medium">{ROLE_LABELS[profile?.role || ''] || profile?.role}</p>
              </div>
              <div>
                <p className="text-muted-color text-xs">Account creato</p>
                <p className="text-white font-medium">
                  {profile?.created_at ? new Date(profile.created_at).toLocaleDateString('it-IT') : 'N/D'}
                </p>
              </div>
              <div>
                <p className="text-muted-color text-xs">2FA</p>
                <p className={`font-medium ${profile?.two_fa_enabled ? 'text-green-400' : 'text-muted-color'}`}>
                  {profile?.two_fa_enabled ? 'Abilitato' : 'Non abilitato'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab: Notifiche */}
      {tab === 'notifiche' && (
        <div className="bg-surface border border-border rounded-2xl p-6 space-y-5 max-w-2xl">
          <h2 className="text-base font-bold text-white">Preferenze Notifiche</h2>
          <div className="space-y-4">
            {[
              { key: 'notifiche_inapp', label: 'Notifiche In-App', desc: 'Ricevi notifiche direttamente nell\'applicazione', icon: 'BellIcon' },
              { key: 'notifiche_email', label: 'Notifiche Email', desc: 'Ricevi notifiche via email', icon: 'EnvelopeIcon' },
              { key: 'notifiche_sms', label: 'Notifiche SMS', desc: 'Ricevi notifiche via SMS (richiede numero di telefono)', icon: 'DevicePhoneMobileIcon' },
            ].map(({ key, label, desc, icon }) => (
              <div key={key} className="flex items-center justify-between p-4 bg-surface-elevated rounded-xl border border-border">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-sky-500/10 flex items-center justify-center">
                    <Icon name={icon as any} size={18} className="text-sky-400" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white">{label}</p>
                    <p className="text-xs text-muted-color">{desc}</p>
                  </div>
                </div>
                <button
                  onClick={() => setNotifForm(f => ({ ...f, [key]: !f[key as keyof typeof f] }))}
                  className={`relative w-11 h-6 rounded-full transition-colors ${notifForm[key as keyof typeof notifForm] ? 'bg-sky-500' : 'bg-surface'} border border-border`}
                >
                  <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${notifForm[key as keyof typeof notifForm] ? 'translate-x-5' : 'translate-x-0.5'}`} />
                </button>
              </div>
            ))}
          </div>
          <div className="flex justify-end">
            <button
              onClick={saveNotifiche}
              disabled={saving}
              className="flex items-center gap-2 bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white px-5 py-2 rounded-lg text-sm font-semibold transition-colors"
            >
              {saving ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Icon name="CheckIcon" size={16} />
              )}
              Salva Preferenze
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
